<template>
  <section class="follow-ups">
    <div class="follow-ups__toolbar">
      <label class="follow-ups__search">
        <i class="fas fa-search" aria-hidden="true"></i>
        <input
          v-model.trim="search"
          type="search"
          placeholder="Rechercher un point ou un responsable"
          aria-label="Rechercher un point de suivi"
        >
      </label>
      <select v-model="statusFilter" class="follow-ups__select" aria-label="Filtrer par état">
        <option value="">Tous les états</option>
        <option v-for="status in statuses" :key="status" :value="status">{{ followUpStatusLabel(status) }}</option>
      </select>
      <select v-model="categoryFilter" class="follow-ups__select" aria-label="Filtrer par catégorie">
        <option value="">Toutes les catégories</option>
        <option v-for="category in categories" :key="category" :value="category">{{ followUpCategoryLabel(category) }}</option>
        <option value="none">Non classé</option>
      </select>
      <span class="follow-ups__count">{{ countLabel }}</span>
      <button
        v-if="!readonly"
        type="button"
        class="meetings-outline meetings-outline--primary meetings-outline--small follow-ups__add"
        :disabled="adding || busy"
        @click="startAdding"
      >
        <i class="fas fa-plus" aria-hidden="true"></i>
        Ajouter un point
      </button>
    </div>

    <div class="follow-ups__table-wrap">
      <table class="follow-ups__table">
        <thead>
          <tr>
            <th
              v-for="column in columns"
              :key="column.key"
              :class="[`follow-ups__col-${column.key}`, { 'follow-ups__sorted': sortKey === column.key }]"
              scope="col"
            >
              <button type="button" class="follow-ups__sort" :aria-sort="ariaSort(column.key)" @click="sortBy(column.key)">
                {{ column.label }}
                <i
                  class="fas follow-ups__sort-icon"
                  :class="sortKey === column.key ? (sortDesc ? 'fa-sort-down' : 'fa-sort-up') : 'fa-sort'"
                  aria-hidden="true"
                ></i>
              </button>
            </th>
            <th v-if="!readonly" class="follow-ups__col-actions" scope="col"><span class="sr-only">Actions</span></th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="adding" class="follow-ups__row follow-ups__row--draft">
            <td>
              <textarea
                ref="draftItem"
                v-model="draft.item"
                class="follow-ups__input follow-ups__input--item"
                rows="2"
                :maxlength="maxItemLength"
                placeholder="Le point à suivre"
                :disabled="busy"
                @keydown.enter.exact.prevent="saveDraft"
                @keydown.esc.prevent="cancelDraft"
              ></textarea>
              <p v-if="draftError" class="follow-ups__error">{{ draftError }}</p>
            </td>
            <td>
              <select v-model="draft.category" class="follow-ups__input" :disabled="busy" aria-label="Catégorie du nouveau point">
                <option :value="null">Non classé</option>
                <option v-for="category in categories" :key="category" :value="category">{{ followUpCategoryLabel(category) }}</option>
              </select>
            </td>
            <td>
              <input v-model="draft.owner" type="text" class="follow-ups__input" :maxlength="maxTextLength" placeholder="Qui" :disabled="busy" aria-label="Responsable du nouveau point" @keydown.enter.prevent="saveDraft" @keydown.esc.prevent="cancelDraft">
            </td>
            <td>
              <input v-model="draft.due" type="text" class="follow-ups__input" :maxlength="maxTextLength" placeholder="Échéance" :disabled="busy" aria-label="Échéance du nouveau point" @keydown.enter.prevent="saveDraft" @keydown.esc.prevent="cancelDraft">
            </td>
            <td>
              <select v-model="draft.status" class="follow-ups__input" :disabled="busy" aria-label="État du nouveau point">
                <option v-for="status in statuses" :key="status" :value="status">{{ followUpStatusLabel(status) }}</option>
              </select>
            </td>
            <td class="follow-ups__origin">Ajouté par un élu</td>
            <td class="follow-ups__actions follow-ups__actions--draft">
              <button type="button" class="meetings-outline meetings-outline--primary meetings-outline--small" :disabled="busy" @click="saveDraft">
                Enregistrer
              </button>
              <button type="button" class="meetings-outline meetings-outline--small" :disabled="busy" @click="cancelDraft">
                Annuler
              </button>
            </td>
          </tr>

          <tr
            v-for="followUp in visibleFollowUps"
            :key="followUp.id"
            class="follow-ups__row"
            :class="{ 'follow-ups__row--saving': pendingId === followUp.id, 'follow-ups__row--saved': savedId === followUp.id }"
          >
            <td class="follow-ups__item">
              <span class="follow-ups__item-text">{{ followUp.item }}</span>
              <p v-if="followUp.update" class="follow-ups__update">{{ followUp.update }}</p>
              <p v-if="errors[followUp.id]" class="follow-ups__error">{{ errors[followUp.id] }}</p>
            </td>
            <td>
              <select
                v-if="!readonly"
                class="follow-ups__input"
                :value="followUp.category || ''"
                :disabled="pendingId === followUp.id"
                :aria-label="`Catégorie du point « ${followUp.item} »`"
                @change="save(followUp, 'category', $event.target.value || null)"
              >
                <option value="">Non classé</option>
                <option v-for="category in categories" :key="category" :value="category">{{ followUpCategoryLabel(category) }}</option>
              </select>
              <span v-else class="follow-ups__category" :class="{ 'follow-ups__category--none': !followUp.category }">
                {{ followUpCategoryLabel(followUp.category) }}
              </span>
            </td>
            <td>
              <input
                v-if="!readonly"
                type="text"
                class="follow-ups__input"
                :value="followUp.owner || ''"
                :maxlength="maxTextLength"
                placeholder="—"
                :disabled="pendingId === followUp.id"
                :aria-label="`Responsable du point « ${followUp.item} »`"
                @change="save(followUp, 'owner', $event.target.value)"
                @keydown.enter.prevent="$event.target.blur()"
              >
              <template v-else>{{ followUp.owner || '—' }}</template>
            </td>
            <td>
              <input
                v-if="!readonly"
                type="text"
                class="follow-ups__input"
                :value="followUp.due || ''"
                :maxlength="maxTextLength"
                placeholder="—"
                :disabled="pendingId === followUp.id"
                :aria-label="`Échéance du point « ${followUp.item} »`"
                @change="save(followUp, 'due', $event.target.value)"
                @keydown.enter.prevent="$event.target.blur()"
              >
              <template v-else>{{ followUp.due || '—' }}</template>
            </td>
            <td>
              <select
                v-if="!readonly"
                class="follow-ups__pill"
                :class="`follow-ups__pill--${followUp.status}`"
                :value="followUp.status"
                :disabled="pendingId === followUp.id"
                :aria-label="`État du point « ${followUp.item} »`"
                @change="save(followUp, 'status', $event.target.value)"
              >
                <option v-for="status in statuses" :key="status" :value="status">{{ followUpStatusLabel(status) }}</option>
              </select>
              <span v-else class="follow-ups__status" :class="`follow-ups__status--${followUp.status}`">
                {{ followUpStatusLabel(followUp.status) }}
              </span>
            </td>
            <td class="follow-ups__origin">
              {{ followUpOriginLabel(followUp.origin) }}
              <span v-if="followUp.source === 'review'" class="follow-ups__reviewed" title="Ajouté ou modifié par un élu">Élu</span>
            </td>
            <td v-if="!readonly" class="follow-ups__actions">
              <i v-if="savedId === followUp.id" class="fas fa-check follow-ups__saved" aria-hidden="true" title="Enregistré"></i>
              <button
                type="button"
                class="follow-ups__delete"
                :disabled="pendingId === followUp.id"
                :title="`Supprimer le point « ${followUp.item} »`"
                @click="confirmRemove(followUp)"
              >
                <i class="fas fa-trash-alt" aria-hidden="true"></i>
              </button>
            </td>
          </tr>

          <tr v-if="!visibleFollowUps.length && !adding">
            <td :colspan="columns.length + (readonly ? 0 : 1)" class="follow-ups__empty">
              {{ followUps.length ? 'Aucun point ne correspond à ces filtres.' : 'Aucun point de suivi n’a été relevé pour cette réunion.' }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </section>
</template>

<script>
import aiService from '@/services/ai.service'
import {
  FOLLOW_UP_STATUS_LABELS,
  FOLLOW_UP_CATEGORY_LABELS,
  followUpStatusLabel,
  followUpCategoryLabel,
  followUpOriginLabel,
  normalizeText,
  escapeHtml,
  validationErrors
} from './meetingHelpers'

/** Mêmes limites que l'API (MeetingFollowUpRequest). */
const MAX_ITEM_LENGTH = 1000
const MAX_TEXT_LENGTH = 160

const COLUMNS = [
  { key: 'item', label: 'Point' },
  { key: 'category', label: 'Catégorie' },
  { key: 'owner', label: 'Qui' },
  { key: 'due', label: 'Échéance' },
  { key: 'status', label: 'État' },
  { key: 'origin', label: 'Origine' }
]

const STATUS_ORDER = ['to_investigate', 'pending', 'decided']

const EDITABLE_FIELDS = ['owner', 'due', 'status', 'category']

const SAVED_FLASH_MS = 1800

/**
 * Onglet « Points de suivi » de la fiche : les points relevés par l'IA (et
 * ceux ajoutés à la main), triés et filtrés côté client, modifiables en ligne
 * (qui, échéance, état, catégorie : enregistrés dès le changement), avec
 * l'ajout d'une ligne vide et la suppression après confirmation. Le composant
 * appelle l'API lui-même et remonte la réunion renvoyée (`change`) pour que la
 * fiche la reprenne ; un refus pendant le traitement (409) remonte `conflict`,
 * les messages d'information passent par `notify`. En lecture seule (réunion
 * en traitement) les champs sont figés.
 */
export default {
  name: 'app-meeting-follow-ups',
  props: {
    meeting: { type: Object, required: true },
    readonly: { type: Boolean, default: false }
  },
  data () {
    return {
      search: '',
      statusFilter: '',
      categoryFilter: '',
      sortKey: null,
      sortDesc: false,
      adding: false,
      draft: this.emptyDraft(),
      draftError: '',
      busy: false,
      pendingId: null,
      savedId: null,
      savedTimer: null,
      errors: {},
      columns: COLUMNS,
      statuses: Object.keys(FOLLOW_UP_STATUS_LABELS),
      categories: Object.keys(FOLLOW_UP_CATEGORY_LABELS),
      maxItemLength: MAX_ITEM_LENGTH,
      maxTextLength: MAX_TEXT_LENGTH
    }
  },
  computed: {
    followUps () {
      return Array.isArray(this.meeting.follow_ups)
        ? this.meeting.follow_ups.filter(followUp => followUp && followUp.item && followUp.id)
        : []
    },
    filteredFollowUps () {
      const needle = normalizeText(this.search)
      return this.followUps.filter(followUp => {
        if (this.statusFilter && followUp.status !== this.statusFilter) {
          return false
        }
        if (this.categoryFilter === 'none' ? followUp.category : (this.categoryFilter && followUp.category !== this.categoryFilter)) {
          return false
        }
        if (needle && !normalizeText(`${followUp.item} ${followUp.owner || ''}`).includes(needle)) {
          return false
        }
        return true
      })
    },
    visibleFollowUps () {
      if (!this.sortKey) {
        return this.filteredFollowUps
      }
      const direction = this.sortDesc ? -1 : 1
      return this.filteredFollowUps.slice().sort((a, b) => this.compare(a, b, this.sortKey, direction))
    },
    countLabel () {
      const total = this.followUps.length
      const shown = this.filteredFollowUps.length
      if (shown !== total) {
        return `${shown} sur ${total} point${total > 1 ? 's' : ''}`
      }
      return `${total} point${total > 1 ? 's' : ''}`
    }
  },
  watch: {
    'meeting.uuid' () {
      this.cancelDraft()
      this.errors = {}
    },
    readonly (readonly) {
      if (readonly) {
        this.cancelDraft()
      }
    }
  },
  beforeDestroy () {
    if (this.savedTimer) {
      window.clearTimeout(this.savedTimer)
    }
  },
  methods: {
    followUpStatusLabel,
    followUpCategoryLabel,
    followUpOriginLabel,
    emptyDraft () {
      return { item: '', owner: '', due: '', status: 'to_investigate', category: null }
    },
    ariaSort (key) {
      if (this.sortKey !== key) {
        return 'none'
      }
      return this.sortDesc ? 'descending' : 'ascending'
    },
    /** Premier clic : tri croissant ; second clic sur la même colonne : sens inversé. */
    sortBy (key) {
      if (this.sortKey === key) {
        this.sortDesc = !this.sortDesc
        return
      }
      this.sortKey = key
      this.sortDesc = false
    },
    /** `direction` vaut 1 (croissant) ou -1 ; les valeurs vides vont en dernier quel que soit le sens. */
    compare (a, b, key, direction) {
      const left = this.sortValue(a, key)
      const right = this.sortValue(b, key)
      if (!left || !right) {
        return left ? -1 : (right ? 1 : 0)
      }
      if (key === 'status') {
        return direction * (STATUS_ORDER.indexOf(a.status) - STATUS_ORDER.indexOf(b.status))
      }
      return direction * left.localeCompare(right, 'fr', { sensitivity: 'base', numeric: true })
    },
    sortValue (followUp, key) {
      switch (key) {
        case 'category':
          return followUp.category ? followUpCategoryLabel(followUp.category) : ''
        case 'origin':
          return followUpOriginLabel(followUp.origin)
        default:
          return String(followUp[key] || '')
      }
    },
    startAdding () {
      if (this.readonly) {
        return
      }
      this.adding = true
      this.draft = this.emptyDraft()
      this.draftError = ''
      this.$nextTick(() => {
        if (this.$refs.draftItem) {
          this.$refs.draftItem.focus()
        }
      })
    },
    cancelDraft () {
      this.adding = false
      this.draft = this.emptyDraft()
      this.draftError = ''
    },
    saveDraft () {
      if (this.busy) {
        return
      }
      const item = String(this.draft.item || '').trim()
      if (!item) {
        this.draftError = 'Indiquez le point à suivre.'
        return
      }
      this.draftError = ''
      this.busy = true
      aiService.addMeetingFollowUp(this.meeting.uuid, {
        item,
        owner: String(this.draft.owner || '').trim() || null,
        due: String(this.draft.due || '').trim() || null,
        status: this.draft.status,
        category: this.draft.category || null
      }).then(payload => {
        this.cancelDraft()
        this.publish(payload)
        this.flashSaved(payload && payload.follow_up ? payload.follow_up.id : null)
        this.$emit('notify', 'is-success', 'Point de suivi ajouté.')
      }).catch(err => {
        this.draftError = this.errorMessage(err, 'Impossible d’ajouter ce point de suivi.')
      }).finally(() => {
        this.busy = false
      })
    },
    /** Un champ modifié en ligne : enregistré aussitôt, le reste de la ligne restant tel quel. */
    save (followUp, field, rawValue) {
      if (this.readonly || !EDITABLE_FIELDS.includes(field)) {
        return
      }
      const value = field === 'status' ? rawValue : (String(rawValue || '').trim() || null)
      if ((followUp[field] || null) === value) {
        return
      }
      this.pendingId = followUp.id
      this.$set(this.errors, followUp.id, '')
      aiService.updateMeetingFollowUp(this.meeting.uuid, followUp.id, { [field]: value }).then(payload => {
        this.publish(payload)
        this.flashSaved(followUp.id)
      }).catch(err => {
        this.$set(this.errors, followUp.id, this.errorMessage(err, 'Impossible d’enregistrer cette modification.'))
        // La valeur affichée revient à celle de la fiche.
        this.$forceUpdate()
      }).finally(() => {
        this.pendingId = null
      })
    },
    confirmRemove (followUp) {
      if (this.readonly || this.pendingId) {
        return
      }
      this.$buefy.dialog.confirm({
        title: 'Supprimer le point de suivi',
        message: `Supprimer le point <b>${escapeHtml(followUp.item)}</b> ? Cette action est définitive.`,
        type: 'is-danger',
        hasIcon: true,
        icon: 'exclamation-triangle',
        cancelText: 'Annuler',
        confirmText: 'Supprimer',
        onConfirm: () => this.remove(followUp)
      })
    },
    remove (followUp) {
      this.pendingId = followUp.id
      aiService.deleteMeetingFollowUp(this.meeting.uuid, followUp.id).then(payload => {
        this.publish(payload)
        this.$emit('notify', 'is-success', 'Point de suivi supprimé.')
      }).catch(err => {
        this.$set(this.errors, followUp.id, this.errorMessage(err, 'Impossible de supprimer ce point de suivi.'))
      }).finally(() => {
        this.pendingId = null
      })
    },
    /** La fiche renvoyée par l'API remplace celle du parent. */
    publish (payload) {
      if (payload && payload.uuid) {
        this.$emit('change', payload)
      }
    },
    flashSaved (id) {
      if (!id) {
        return
      }
      this.savedId = id
      if (this.savedTimer) {
        window.clearTimeout(this.savedTimer)
      }
      this.savedTimer = window.setTimeout(() => {
        if (this.savedId === id) {
          this.savedId = null
        }
      }, SAVED_FLASH_MS)
    },
    errorMessage (err, fallback) {
      const status = err && err.response ? err.response.status : null
      if (status === 409) {
        this.$emit('conflict')
        return 'Les points de suivi ne peuvent pas être modifiés pendant le traitement.'
      }
      if (status === 404) {
        this.$emit('conflict')
        return 'Ce point n’existe plus : la fiche a été rechargée.'
      }
      if (status === 422) {
        const { fields, general } = validationErrors(err, ['item', 'owner', 'due', 'status', 'category'])
        const messages = [].concat(...Object.keys(fields).map(key => fields[key]), general)
        return messages[0] || fallback
      }
      if (status === 403) {
        return 'Vous n’avez pas le rôle requis pour modifier les points de suivi.'
      }
      return fallback
    }
  }
}
</script>

<style scoped>
.follow-ups__toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.6rem 0.75rem;
  margin-bottom: 0.9rem;
}

.follow-ups__search {
  display: flex;
  flex: 1 1 16rem;
  align-items: center;
  gap: 0.5rem;
  max-width: 22rem;
  min-width: 12rem;
  padding: 0 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 0.55rem;
  background: #ffffff;
}

.follow-ups__search:focus-within {
  border-color: #2563eb;
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.12);
}

.follow-ups__search i {
  color: #9ca3af;
  font-size: 0.8rem;
}

.follow-ups__search input {
  width: 100%;
  min-width: 0;
  padding: 0.5rem 0;
  border: 0;
  box-shadow: none;
  font-size: 0.875rem;
  outline: none;
}

.follow-ups__select {
  min-height: 2.35rem;
  padding: 0 0.6rem;
  border: 1px solid #d1d5db;
  border-radius: 0.55rem;
  background: #ffffff;
  color: #111827;
  font-size: 0.875rem;
}

.follow-ups__count {
  color: #6b7280;
  font-size: 0.8rem;
  white-space: nowrap;
}

.follow-ups__add {
  margin-left: auto;
}

.follow-ups__table-wrap {
  overflow: auto;
  border: 1px solid #e5e7eb;
  border-radius: 0.65rem;
}

.follow-ups__table {
  width: 100%;
  /* Somme des colonnes : en dessous, défilement horizontal plutôt que des champs tronqués. */
  min-width: 1160px;
  border-collapse: separate;
  border-spacing: 0;
}

.follow-ups__table th {
  padding: 0;
  background: #f9fafb;
  color: #4b5563;
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 0.03em;
  text-align: left;
  text-transform: uppercase;
}

.follow-ups__sort {
  display: flex;
  width: 100%;
  align-items: center;
  gap: 0.4rem;
  padding: 12px 14px;
  border: 0;
  background: transparent;
  color: inherit;
  font: inherit;
  letter-spacing: inherit;
  text-align: left;
  text-transform: inherit;
  cursor: pointer;
}

.follow-ups__sort:hover,
.follow-ups__sorted .follow-ups__sort {
  color: #1d4ed8;
}

.follow-ups__sort:focus-visible {
  outline: 2px solid #2563eb;
  outline-offset: -2px;
}

.follow-ups__sort-icon {
  color: #c4c9d2;
  font-size: 0.75rem;
}

.follow-ups__sorted .follow-ups__sort-icon {
  color: #1d4ed8;
}

.follow-ups__col-item {
  min-width: 20rem;
}

.follow-ups__col-category {
  width: 13rem;
}

.follow-ups__col-owner,
.follow-ups__col-due {
  width: 9rem;
}

.follow-ups__col-status {
  width: 8rem;
}

.follow-ups__col-origin {
  width: 9.5rem;
}

.follow-ups__col-actions {
  width: 3.5rem;
}

.follow-ups__table td {
  padding: 8px 10px;
  border-top: 1px solid #e5e7eb;
  color: #111827;
  font-size: 14px;
  line-height: 1.25rem;
  vertical-align: top;
}

.follow-ups__item {
  padding-top: 13px;
  padding-left: 14px;
}

.follow-ups__row--saving td {
  opacity: 0.6;
}

.follow-ups__row--saved td {
  background: #f0fdf4;
  transition: background 0.3s ease;
}

.follow-ups__row--draft td {
  background: #eff6ff;
}

.follow-ups__update {
  margin: 0.25rem 0 0;
  color: #4b5563;
  font-size: 0.8rem;
  font-style: italic;
}

.follow-ups__error {
  margin: 0.25rem 0 0;
  color: #b91c1c;
  font-size: 0.8rem;
}

.follow-ups__input {
  width: 100%;
  min-height: 2.2rem;
  padding: 0.3rem 0.55rem;
  border: 1px solid transparent;
  border-radius: 0.5rem;
  background: transparent;
  color: #111827;
  font: inherit;
  font-size: 0.875rem;
  transition: border-color 0.15s ease, background 0.15s ease;
}

.follow-ups__input:hover:not(:disabled) {
  border-color: #d1d5db;
  background-color: #ffffff;
}

.follow-ups__input:focus {
  outline: none;
  border-color: #2563eb;
  background-color: #ffffff;
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.15);
}

.follow-ups__input:disabled {
  cursor: not-allowed;
  color: #6b7280;
}

.follow-ups__input::placeholder {
  color: #9ca3af;
}

.follow-ups__row--draft .follow-ups__input {
  border-color: #d1d5db;
  background: #ffffff;
}

.follow-ups__input--item {
  resize: vertical;
}

/* L'état garde les couleurs de ses pilules, même en sélecteur. */
.follow-ups__status,
.follow-ups__pill {
  display: inline-block;
  padding: 5px 10px;
  border-radius: 999px;
  background-color: #f3f4f6;
  color: #4b5563;
  font-size: 13px;
  font-weight: 600;
  white-space: nowrap;
}

.follow-ups__pill {
  padding-right: 1.7rem;
  border: 1px solid transparent;
  appearance: none;
  -webkit-appearance: none;
  background-image: url("data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6'%3E%3Cpath d='M1 1l4 4 4-4' fill='none' stroke='currentColor' stroke-width='1.5'/%3E%3C/svg%3E");
  background-position: right 0.6rem center;
  background-repeat: no-repeat;
  font-family: inherit;
  cursor: pointer;
}

.follow-ups__pill:hover:not(:disabled) {
  border-color: rgba(17, 24, 39, 0.15);
}

.follow-ups__pill:focus {
  outline: none;
  border-color: #2563eb;
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.15);
}

.follow-ups__pill:disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

.follow-ups__status--decided,
.follow-ups__pill--decided {
  background-color: #dcfce7;
  color: #166534;
}

.follow-ups__status--pending,
.follow-ups__pill--pending {
  background-color: #fef3c7;
  color: #92400e;
}

.follow-ups__status--to_investigate,
.follow-ups__pill--to_investigate {
  background-color: #dbeafe;
  color: #1d4ed8;
}

.follow-ups__category--none {
  color: #9ca3af;
}

.follow-ups__origin {
  color: #4b5563;
  padding-top: 13px;
  white-space: nowrap;
}

/* Point ajouté ou modifié par un élu : étiquette discrète à côté de l'origine. */
.follow-ups__reviewed {
  display: inline-block;
  margin-left: 0.35rem;
  padding: 0.05rem 0.45rem;
  border: 1px solid #c7d2fe;
  border-radius: 999px;
  background: #eef2ff;
  color: #4338ca;
  font-size: 0.72rem;
  font-weight: 600;
  vertical-align: middle;
}

.follow-ups__actions {
  padding-top: 10px;
  text-align: right;
  white-space: nowrap;
}

.follow-ups__actions--draft {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  align-items: stretch;
}

.follow-ups__saved {
  margin-right: 0.4rem;
  color: #16a34a;
  font-size: 0.8rem;
}

.follow-ups__delete {
  padding: 0.3rem 0.5rem;
  border: 0;
  border-radius: 0.4rem;
  background: transparent;
  color: #9ca3af;
  cursor: pointer;
  transition: color 0.15s ease, background 0.15s ease;
}

.follow-ups__delete:hover:not(:disabled) {
  background: #fef2f2;
  color: #b91c1c;
}

.follow-ups__delete:disabled {
  cursor: not-allowed;
  opacity: 0.5;
}

.follow-ups__empty {
  padding: 2rem 1rem;
  color: #4b5563;
  text-align: center;
}

.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
}

@media (max-width: 1100px) {
  .follow-ups__search {
    flex: 1 1 100%;
    max-width: 100%;
  }

  .follow-ups__add {
    margin-left: 0;
  }
}
</style>
